import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator } from 'react-native-paper';
import { useWindowDimensions, View, Text } from 'react-native';
import { useRouter, useNavigation } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { useFilter } from '@/data/store/useFilterStore';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { Table } from '@/components/General/Table';
import Avatar from '@/components/People/Avatar';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { DateTime } from 'luxon';
import PeopleAdvancedFilter from '@/components/Filters/PeopleAdvancedFilter';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import { exportPeopleData, archivePerson, getAllPeopleIdsForSelection, getAllPeopleForSelection, getPeopleByIds, getReachUserIdsAmong } from '@/data/people';
import { showAlert } from '@/utils/showAlert';
import Toast from 'react-native-toast-message';
import ExportFieldsModal from '@/components/General/ExportFieldsModal';
import Unicon from '@/components/General/Unicon';
import usePlatform from '@/utils/hooks/usePlatform';
import usePeopleBatchActions from '@/utils/hooks/usePeopleBatchActions';
import useTablePreferencesStore from '@/data/store/useTablePreferencesStore';
import PeopleColumnSettingsModal from '@/components/People/PeopleColumnSettingsModal';
import IconButton from '@/components/General/IconButton';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg, sanitizeFiltersForPlan } from '@/utils/organizationEntitlements';

const TABLE_PREFERENCES_ID = 'people-directory';
const ADDRESS_COLUMN_IDS = ['street', 'streetLine2', 'city', 'state', 'zip'];
const DEFAULT_COLUMN_IDS = ['name', 'gender', 'dateOfBirth', 'phone', 'email', ...ADDRESS_COLUMN_IDS];

const hasValue = (value) => value !== null && value !== undefined && value !== '';

const parseDate = (value) => {
    const normalized = Array.isArray(value) ? value[0] : value;
    if (!hasValue(normalized)) return null;
    if (normalized instanceof Date) return DateTime.fromJSDate(normalized);
    if (typeof normalized !== 'string') return null;
    const localDate = DateTime.fromFormat(normalized, 'MM/dd/yyyy');
    if (localDate.isValid) return localDate;
    const sqlDate = DateTime.fromSQL(normalized);
    if (sqlDate.isValid) return sqlDate;
    const isoDate = DateTime.fromISO(normalized);
    return isoDate.isValid ? isoDate : null;
};

const formatDate = (value, format = DateTime.DATE_MED) => parseDate(value)?.toLocaleString(format) || '';

const formatCustomFieldValue = (value, field) => {
    if (!hasValue(value)) return '';
    if (field.type === 'Checkbox' || typeof value === 'boolean') return value === true || value === 'true' ? 'Yes' : 'No';
    if (field.type === 'Date') return formatDate(value);
    if (field.type === 'Datetime') return formatDate(value, DateTime.DATETIME_MED);
    if (Array.isArray(value)) {
        return value.map(item => {
            const option = field.options?.find(candidate => (typeof candidate === 'object' ? candidate.value : candidate) === item);
            return option ? (typeof option === 'object' ? option.label : option) : item;
        }).join(', ');
    }
    return String(value);
};

export default function PeopleScreen(){

    /*** Theme & Dimensions ***/
    const { theme: { colors, scheme, fonts }} = useUnistyles(); 
    const router = useRouter();
    const navigation = useNavigation();
    const [searchText, setSearchText] = useState("");
    const [includeArchived, setIncludeArchived] = useState(false);

    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
    const [sortConfig, setSortConfig] = useState(null);
    const { isPortrait } = usePlatform();
    const { width: windowWidth, height: windowHeight } = useWindowDimensions();
    const isLandscapeLayout = windowWidth > windowHeight;


    const listRef = useRef();
    const seenPeopleMap = useRef(new Map());
    const filterState = useFilter('people');
    const queryClient = useQueryClient();
    const { entitlements } = useOrganizationEntitlements();
    const isFree = isFreeOrg(entitlements);
    const { data: loggedInUser } = Query.people.loggedInUser();
    const canViewBasic = loggedInUser?.isAdmin || loggedInUser?.can?.('View Basic Info');
    const canViewContact = loggedInUser?.isAdmin || loggedInUser?.can?.('View Contact Info');
    const canViewCustom = !isFree && (loggedInUser?.isAdmin || loggedInUser?.can?.('View Custom Fields'));
    const { data: customFields = [] } = Query.personCustomFields.all({ options: { enabled: !!canViewCustom } });
    const storedColumnIds = useTablePreferencesStore(state => state.tables[TABLE_PREFERENCES_ID]?.columnIds);
    const storedViewMode = useTablePreferencesStore(state => state.tables[TABLE_PREFERENCES_ID]?.viewMode);
    const hydrateTablePreferences = useTablePreferencesStore(state => state.hydrate);
    const setStoredColumnIds = useTablePreferencesStore(state => state.setColumnIds);
    const setStoredViewMode = useTablePreferencesStore(state => state.setViewMode);
    const viewMode = isLandscapeLayout ? 'table' : (storedViewMode || 'cards');

    useEffect(() => {
        hydrateTablePreferences();
    }, [hydrateTablePreferences]);

    const combinedFilters = useMemo(() => ({
        ...sanitizeFiltersForPlan(filterState.peopleFilters, entitlements),
        includeArchived: includeArchived ? 'true' : 'false'
    }), [filterState.peopleFilters, includeArchived, entitlements]);

    const { data: filteredPeople, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = Query.people.all({
        searchTerm: searchText,
        filters: combinedFilters,
        filterLogic: filterState.filterLogic,
        sortKey: sortConfig?.key,
        sortDirection: sortConfig?.direction,
    });

    const flatData = useMemo(() => filteredPeople?.pages.flatMap(page => page.data), [filteredPeople]);
    const totalCount = useMemo(() => filteredPeople?.pages[0].count, [filteredPeople]);

    useEffect(() => {
        seenPeopleMap.current.clear();
        flatData?.forEach(p => { if (p?.id) seenPeopleMap.current.set(p.id, p); });
    }, [combinedFilters, filterState.filterLogic, searchText, sortConfig]);

    useEffect(() => {
        flatData?.forEach(p => { if (p?.id) seenPeopleMap.current.set(p.id, p); });
    }, [flatData]);

    /*** Effects ***/
    useEffect(() => {
        navigation.setOptions({
            headerRight: () => (
                    <DropdownMenu
                        options={[
                            {
                                name: "Add New Person",
                                onSelect: () => router.push("/people/create"),
                                icon: dropdownIcons.create
                            },
                            ...(loggedInUser?.isAdmin || loggedInUser?.can?.("Edit People") ? [{
                                name: "Merge People",
                                icon: { android: "git_merge", ios: "arrow.merge", web: "git-merge-outline" },
                                onSelect: () => router.push("/settings/organization/merge-people")
                            }] : []),
                            {
                                name: "Export to CSV",
                                icon: dropdownIcons.export,
                                onSelect: () => setExportModalOpen(true)
                            },
                            {
                                name: includeArchived ? "Hide Archived" : "Show Archived",
                                icon: dropdownIcons.archive,
                                onSelect: () => setIncludeArchived(!includeArchived)
                            }
                        ]}
                    />
            ),
            headerSearchBarOptions: null
        });
    }, [navigation, scheme, flatData, includeArchived, loggedInUser])


    const refreshList = () => {
        queryClient.invalidateQueries({queryKey: QueryKeys.people.all({searchTerm: searchText, filters: combinedFilters, filterLogic: filterState.filterLogic, sortKey: sortConfig?.key, sortDirection: sortConfig?.direction})});
    }


    const allColumnDefinitions = useMemo(() => {
        const cellText = { fontSize: 14, color: colors.textSecondary };
        const nameRenderer = (item) => (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <Avatar size={34} profile={item} disableBorder />
                <View style={{ flex: 1, minWidth: 0 }}>
                    <Text numberOfLines={1} style={{ fontSize: 14, fontFamily: fonts.heading, color: item.isArchived ? colors.textTertiary : colors.text }}>
                        {item.firstName} {item.lastName}
                    </Text>
                    {item.isArchived && (
                        <View style={styles.archivedBadge}>
                            <Unicon name="archive" size={12} colorVariant="error" />
                            <Text style={styles.archivedText}>Archived</Text>
                        </View>
                    )}
                </View>
            </View>
        );
        const addressFieldRenderer = field => item => <Text style={cellText}>{item.Address?.[field] || ''}</Text>;
        const phoneRenderer = (item) => item.Phones?.length
            ? item.Phones.map((phone, index) => <Text key={`${phone.phoneNumber}-${index}`} style={cellText}>{phone.phoneNumber}</Text>)
            : <Text style={cellText}></Text>;
        const phoneCardRenderer = (item) => <Text style={cellText}>{item.Phones?.[0]?.phoneNumber || ''}</Text>;
        const dateRenderer = (accessor) => (item) => <Text style={cellText}>{formatDate(item[accessor])}</Text>;

        return [
            { id: 'name', value: 'Name', width: 250, accessor: 'name', sortable: true, cardPrimary: true, renderer: nameRenderer },
            ...(canViewBasic ? [
                { id: 'gender', value: 'Gender', accessor: 'gender', sortable: true },
                { id: 'dateOfBirth', value: 'Date of Birth', accessor: 'dateOfBirth', sortable: true, sortType: 'date', renderer: dateRenderer('dateOfBirth') },
                { id: 'age', value: 'Age', accessor: 'age', sortable: true, sortType: 'number', renderer: (item) => {
                    const birthDate = item.dateOfBirth ? DateTime.fromISO(item.dateOfBirth) : null;
                    const age = birthDate?.isValid ? Math.floor(DateTime.now().diff(birthDate, 'years').years) : '';
                    return <Text style={cellText}>{age}</Text>;
                } },
                // Attendance columns are paid-only — stripped/rejected by filter_people on Free.
                ...(isFree ? [] : [
                    { id: 'timesAttended', value: 'Times Attended', accessor: 'timesAttended', sortable: true, sortType: 'number' },
                    { id: 'mostRecentVisit', value: 'Last Visit', accessor: 'mostRecentVisit', sortable: true, sortType: 'date', renderer: dateRenderer('mostRecentVisit') },
                    { id: 'firstVisit', value: 'First Visit', accessor: 'firstVisit', sortable: true, sortType: 'date', renderer: dateRenderer('firstVisit') },
                ]),
                { id: 'hasAccount', value: 'Has Account', accessor: 'hasAccount', sortable: true, renderer: (item) => <Text style={cellText}>{hasValue(item.hasAccount) ? (item.hasAccount ? 'Yes' : 'No') : ''}</Text> },
                { id: 'createdAt', value: 'Date Added', accessor: 'createdAt', sortable: true, sortType: 'date', renderer: dateRenderer('createdAt') },
                { id: 'updatedAt', value: 'Last Updated', accessor: 'updatedAt', sortable: true, sortType: 'date', renderer: dateRenderer('updatedAt') },
            ] : []),
            ...(canViewContact ? [
                { id: 'email', value: 'Email', accessor: 'email', sortable: true },
                { id: 'phone', value: 'Phone', accessor: 'phone', sortable: true, renderer: phoneRenderer, cardRenderer: phoneCardRenderer },
                { id: 'street', value: 'Street', accessor: 'street', sortable: true, renderer: addressFieldRenderer('street') },
                { id: 'streetLine2', value: 'Street Line 2', accessor: 'streetLine2', sortable: true, renderer: addressFieldRenderer('streetLine2') },
                { id: 'city', value: 'City', accessor: 'city', sortable: true, renderer: addressFieldRenderer('city') },
                { id: 'state', value: 'State', accessor: 'state', sortable: true, renderer: addressFieldRenderer('state') },
                { id: 'zip', value: 'ZIP', accessor: 'zip', sortable: true, renderer: addressFieldRenderer('zip') },
            ] : []),
            ...(canViewCustom ? customFields
                .filter(field => field.type !== 'File Upload')
                .map(field => ({
                    id: `custom:${field.id}`,
                    value: field.name,
                    accessor: `custom:${field.id}`,
                    sortable: true,
                    sortType: field.type === 'Number' ? 'number' : (field.type === 'Date' || field.type === 'Datetime' ? 'date' : 'text'),
                    section: field.Section?.name || 'Custom fields',
                    renderer: (item) => <Text style={cellText}>{formatCustomFieldValue(item.customFieldValues?.[field.id], field)}</Text>,
                })) : []),
        ];
    }, [canViewBasic, canViewContact, canViewCustom, isFree, colors, customFields, fonts]);

    const selectedColumnIds = useMemo(() => {
        const availableIds = new Set(allColumnDefinitions.map(column => column.id));
        const storedIds = storedColumnIds?.length ? storedColumnIds : DEFAULT_COLUMN_IDS;
        const preferredIds = storedIds.flatMap(id => id === 'address' ? ADDRESS_COLUMN_IDS : [id]);
        return ['name', ...preferredIds.filter(id => id !== 'name' && availableIds.has(id))];
    }, [allColumnDefinitions, storedColumnIds]);

    const columns = useMemo(() => {
        const columnMap = new Map(allColumnDefinitions.map(column => [column.id, column]));
        return selectedColumnIds.map(id => columnMap.get(id)).filter(Boolean);
    }, [allColumnDefinitions, selectedColumnIds]);

    const saveColumns = (columnIds) => {
        setStoredColumnIds(TABLE_PREFERENCES_ID, columnIds);
        if (sortConfig && !columnIds.includes(sortConfig.key)) setSortConfig(null);
    };

    const [selectedPeopleForExport, setSelectedPeopleForExport] = useState(null);

    const handleExport = (selectedFields) => {
        exportPeopleData({
            ...(selectedPeopleForExport
                ? { people: selectedPeopleForExport }
                : { totalCount, searchTerm: searchText, filters: sanitizeFiltersForPlan(filterState.peopleFilters, entitlements), filterLogic: filterState.filterLogic }
            ),
            selectedFields
        })
    }

    const getSelectedPeopleIds = async (mode, ids) => {
        if (mode === 'include') {
            return Array.from(ids);
        }
        return getAllPeopleIdsForSelection({
            searchTerm: searchText,
            filters: combinedFilters,
            filterLogic: filterState.filterLogic,
            excludeIds: Array.from(ids),
        });
    };

    const getSelectedPeople = async (mode, ids) => {
        if (mode === 'include') {
            const idArray = Array.from(ids).filter(Boolean);
            const cached = [];
            const missingIds = [];
            idArray.forEach(id => {
                const person = seenPeopleMap.current.get(id);
                if (person) cached.push(person);
                else missingIds.push(id);
            });
            // Selections persist across search/filter changes, but seenPeopleMap only
            // holds currently-visible rows. Fetch any selected IDs that scrolled out of
            // view so batch actions operate on every selected person, not just visible ones.
            const fetched = missingIds.length > 0
                ? await getPeopleByIds(missingIds)
                : [];
            return [...cached, ...fetched];
        }
        return getAllPeopleForSelection({
            searchTerm: searchText,
            filters: combinedFilters,
            filterLogic: filterState.filterLogic,
            excludeIds: Array.from(ids),
            returnFields: ['id', 'firstName', 'lastName', 'email', 'Phones', 'Address', 'avatarUpdatedAt', 'avatarStorageKey'],
        });
    };

    const { peopleBatchActions: sharedPeopleActions, modalElements } = usePeopleBatchActions({
        getSelectedPersonIds: getSelectedPeopleIds,
        getSelectedPeople,
    });

    const peopleBatchActions = [
        {
            name: "Export Selected",
            icon: dropdownIcons.export,
            onSelect: async (mode, ids, _selectedCount) => {
                const selected = await getSelectedPeople(mode, ids);
                setSelectedPeopleForExport(selected.length > 0 ? selected : null);
                setExportModalOpen(true);
            }
        },
        ...(loggedInUser?.can?.("Edit People") ? [{
            name: "Edit People",
            icon: dropdownIcons.edit,
            onSelect: async (mode, ids, _selectedCount) => {
                const selectedIds = await getSelectedPeopleIds(mode, ids);
                if (selectedIds.length === 0) {
                    Toast.show({ type: 'error', text2: 'No people found for selected items.' });
                    return;
                }
                router.push({ pathname: '/people/create', params: { batchIds: JSON.stringify(selectedIds) } });
            }
        }] : []),
        ...sharedPeopleActions,
        {
            name: "Archive",
            icon: dropdownIcons.archive,
            destructive: true,
            onSelect: async (mode, ids, selectedCount) => {
                const selectedIds = await getSelectedPeopleIds(mode, ids);
                showAlert({
                    title: `Archive ${selectedCount} ${selectedCount === 1 ? 'Person' : 'People'}?`,
                    subtitle: `This will archive ${selectedCount === 1 ? 'this person' : 'these people'}. They will no longer appear in active lists.`,
                    confirmText: 'Archive',
                    cancelText: 'Cancel',
                    isDestructive: true,
                    onConfirm: async () => {
                        try {
                            const reachUserIds = await getReachUserIdsAmong(selectedIds);
                            const archiveIds = selectedIds.filter(id => !reachUserIds.includes(id));
                            const results = await Promise.allSettled(archiveIds.map(id => archivePerson(id)));
                            const archivedCount = results.filter(r => r.status === 'fulfilled').length;
                            const failedCount = results.filter(r => r.status === 'rejected').length;

                            queryClient.invalidateQueries({ queryKey: QueryKeys.people.all() });

                            if (archivedCount > 0) {
                                Toast.show({ type: 'success', text2: `${archivedCount} ${archivedCount === 1 ? 'person' : 'people'} archived.` });
                            }
                            if (reachUserIds.length > 0) {
                                Toast.show({
                                    type: reachUserIds.length === 1 ? 'info' : 'error',
                                    text1: `${reachUserIds.length} ${reachUserIds.length === 1 ? 'person was' : 'people were'} not archived.`,
                                    text2: 'Reach users cannot be archived. If users are connected to the wrong profiles, a Reach administrator must change the connected profile.',
                                    visibilityTime: 6000
                                });
                            }
                            if (failedCount > 0) {
                                Toast.show({ type: 'error', text2: `Failed to archive ${failedCount} ${failedCount === 1 ? 'person' : 'people'}. Please try again.` });
                            }
                        } catch {
                            Toast.show({ type: 'error', text2: 'Failed to archive some people. Please try again.' });
                        }
                    }
                });
            }
        },
    ];

    return (
        <View style={{flex:1, width:"100%",  backgroundColor:colors.background}}>
            <ExportFieldsModal
                isOpen={exportModalOpen}
                onClose={() => { setExportModalOpen(false); setSelectedPeopleForExport(null); }}
                onExport={handleExport}
                title="Export People"
            />
            <PeopleColumnSettingsModal
                isOpen={columnSettingsOpen}
                onClose={() => setColumnSettingsOpen(false)}
                columns={allColumnDefinitions}
                selectedColumnIds={selectedColumnIds}
                defaultColumnIds={DEFAULT_COLUMN_IDS.filter(id => allColumnDefinitions.some(column => column.id === id))}
                onSave={saveColumns}
            />
            {modalElements}
            <Table
                noBackground={isPortrait}
                    title="Member Directory"
                    totalCount={totalCount}
                    description="This is a list of everyone in your organizations database"
                    headerRightContent={
                        <View style={styles.headerActions}>
                            <PeopleAdvancedFilter filterId="people" />
                            <IconButton
                                iconName="settings-outline"
                                iconSize={22}
                                type="textSecondary"
                                tooltipText="Choose directory columns"
                                testID="PeopleColumnSettings"
                                onPress={() => setColumnSettingsOpen(true)}
                            />
                        </View>
                    }
                    columns={columns}
                    sortConfig={sortConfig}
                    onSortChange={setSortConfig}
                    enableCards={!isLandscapeLayout}
                    viewMode={viewMode}
                    onViewModeChange={(mode) => setStoredViewMode(TABLE_PREFERENCES_ID, mode)}
                    cardHeaderRenderer={columns[0]?.renderer}
                    cardAccentColor={colors.people}
                    onRowClick={(person) => router.push("/people/" + person.id)}
                    contentInsetAdjustmentBehavior="automatic"
                    showsVerticalScrollIndicator={false}
                    keyboardShouldPersistTaps="handled"
                    estimatedItemSize={60}
                    data={flatData ?? []}
                    ref={listRef}
                    keyExtractor={(item) => item.id}
                    removeClippedSubviews={false}
                    // renderItem={({item}) => <RenderItem item={item} />}
                    progressViewOffset={1000}
                    refreshing={false}
                    onRefresh={refreshList}
                    onEndReachedThreshold={0.5}
                    onSearchChange={setSearchText}
                    onEndReached={() => {
                        if(!isFetching && hasNextPage) fetchNextPage()
                    }}
                    isLoading={(isFetching || isFetchingNextPage)}
                    batchSelection
                    batchActions={peopleBatchActions}
                    ListFooterComponent={isFetchingNextPage &&
                        <View style={{alignContent:'center', justifyContent:'center', paddingVertical: 20}}>
                            <ActivityIndicator size="large" color={colors.primary}/>
                        </View>
                    }
                />
        </View>

    )
}


const styles = StyleSheet.create((theme) => ({
    headerActions: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm },
    archivedBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: theme.colors.errorContainer, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 4, marginTop: 6, alignSelf: 'flex-start', gap: 4 },
    archivedText: { color: theme.colors.error, fontSize: 11, fontFamily: theme.fonts.bold },
}))